// import { setFormData, getFormData } from '@/helpers'
import { inject, nextTick } from 'vue'
import { useUserStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useTranslation } from 'i18next-vue'
import { Cookies } from 'quasar' // eslint-disable-line
import { generateCodeVerifier, generateCodeChallenge, generateState } from '@/helpers'

export default () => {
  const $axios = inject('$axios')
  const { fetchUser } = useUserStore()
  const { id: currentUserId, isAuthenticated, fullName } = storeToRefs(useUserStore())
  const { t } = useTranslation('auth')

  // Shared in-flight refresh promise (not store state — a Promise can't be
  // persisted, and a reactive isRefreshing boolean caused the actual bug:
  // concurrent 401s that lost the race used to just bail out with `false`
  // instead of waiting for this same refresh and retrying with its result,
  // silently dropping most of a page's requests whenever the access token
  // was stale.
  let refreshPromise = null

  // RFC 6749 requires this to match byte-for-byte between the authorize and
  // token requests — computed once here rather than duplicated at each call
  // site, where two hand-maintained copies would be free to drift.
  const OAUTH_REDIRECT_URI = `${window.location.origin}/auth/oauth/callback`

  return {
    /**
     * Clear cookies set by authentication process client-side.
     */
    getXsrfCookie: function () {
      return Cookies.get('XSRF-TOKEN')
    },

    /**
     * Clear cookies set by authentication process client-side.
     */
    clearCookies: function () {
      Cookies.remove('some_cookie')
    },

    /**
     * Clear cookies set by authentication process client-side.
     */
    clearTokens: function () {
      this.accessToken = null
      this.accessTokenExpiresAt = null
      this.refreshToken = null
    },

    /**
     * Store OAuth2 tokens from a Passport `/oauth/token` response.
     *
     * @param {Object} data
     * @param {string} data.access_token
     * @param {string} data.refresh_token
     * @param {number} data.expires_in - Access token TTL in seconds from now.
     */
    storeTokens: async function ({ access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn }) {
      this.accessToken = accessToken
      this.accessTokenExpiresAt = Date.now() + expiresIn * 1000
      this.refreshToken = refreshToken
    },

    /**
     * Start the Authorization Code + PKCE dance against Passport, via a real
     * top-level navigation rather than fetch()/axios. A script-initiated
     * request can't be used here: once a fetch is cross-origin, the browser
     * re-validates CORS on every redirect hop it follows — even one that
     * lands back on this app's own origin, which never sends (and has no
     * reason to send) CORS headers for a plain page load. A real navigation
     * sidesteps this entirely, since CORS only governs script-initiated
     * requests. code_verifier/state have to survive the reload that
     * triggers, so they go in sessionStorage — Pinia state doesn't survive
     * a full navigation either.
     */
    beginOAuthLogin: async function (intent) {
      const codeVerifier = generateCodeVerifier()
      const state = generateState()
      sessionStorage.setItem('oauth_code_verifier', codeVerifier)
      sessionStorage.setItem('oauth_state', state)
      sessionStorage.setItem('oauth_intent', intent)

      const authorizeUrl = new URL('/oauth/authorize', import.meta.env.API_HOST_URL)
      authorizeUrl.searchParams.set('client_id', import.meta.env.OAUTH_CLIENT_ID)
      authorizeUrl.searchParams.set('redirect_uri', OAUTH_REDIRECT_URI)
      authorizeUrl.searchParams.set('response_type', 'code')
      authorizeUrl.searchParams.set('code_challenge', await generateCodeChallenge(codeVerifier))
      authorizeUrl.searchParams.set('code_challenge_method', 'S256')
      authorizeUrl.searchParams.set('state', state)

      window.location.href = authorizeUrl.toString()
    },

    /**
     * Complete the PKCE exchange on return from `beginOAuthLogin`, called
     * from the `/auth/oauth/callback` page with the `code`/`state` query
     * params Passport redirected back with.
     */
    finishOAuthLogin: async function ({ code, state }) {
      const codeVerifier = sessionStorage.getItem('oauth_code_verifier')
      const expectedState = sessionStorage.getItem('oauth_state')
      const intent = sessionStorage.getItem('oauth_intent') ?? 'login'
      sessionStorage.removeItem('oauth_code_verifier')
      sessionStorage.removeItem('oauth_state')
      sessionStorage.removeItem('oauth_intent')

      if (!codeVerifier || !state || state !== expectedState) {
        throw new Error('OAuth state mismatch')
      }

      const { data: tokens } = await $axios.post('/oauth/token', {
        grant_type: 'authorization_code',
        client_id: import.meta.env.OAUTH_CLIENT_ID,
        redirect_uri: OAUTH_REDIRECT_URI,
        code_verifier: codeVerifier,
        code
      })

      await this.storeTokens(tokens)
      $axios.defaults.headers.Authorization = `Bearer ${this.accessToken}`

      try {
        await fetchUser()
      } catch (err) {
        // Tokens are valid but the user resource never loaded — don't leave
        // them persisted, or restoreSession will treat this as a live
        // session on the next reload despite fetchUser having never
        // actually succeeded.
        this.clearTokens()
        delete $axios.defaults.headers.Authorization
        throw err
      }

      this.scheduleRefresh()

      const messageKey = intent === 'register' ? 'register' : 'login'
      this.$toast({
        type: 'success',
        title: t(`Response.${messageKey}.success.title`, { fullName: fullName.value }),
        caption: t(`Response.${messageKey}.success.caption`)
      })

      this.router.push({ name: 'App' })
    },

    redirect: async function (to) {
    },

    /**
     * Handle request error.
     *
     * @param {Object} err
     * @returns {Object} The response object from the server.
     */
    handleRequestError: function (err) {
      console.error(err)
      const message = err.response?.data?.message
      const title = message ? t(`${message}.title`) : null
      // Not every backend error is one of our namespaced i18n keys — Laravel's
      // built-in validation/password-broker responses (e.g. an invalid or
      // expired reset token) return an already-human-readable plain string
      // instead. i18next returns the lookup key itself when it can't resolve
      // one, so an unchanged title here means `message` was never a key.
      if (message && title !== `${message}.title`) {
        this.$toast({
          type: 'error',
          title,
          caption: t(`${message}.caption`)
        })
      } else if (message || !err.response) {
        // No response at all (thrown before/without a request — a rejected
        // crypto.subtle call, sessionStorage blocked in a private tab, a
        // network failure) would otherwise fail completely silently: no
        // toast, no navigation, just a console.error nobody sees.
        this.$toast({
          type: 'error',
          title: t('Response.generic.error.title'),
          caption: t('Response.generic.error.caption')
        })
      }
      return err.response
    },

    /**
     * Register a new user
     *
     * @param {Object} data
     * @param {string} data.name - The first name of the user.
     * @param {string} data.surname - The last name of the user.
     * @param {string} data.email - The email address of the user.
     * @param {string} data.phoneNumber - The phone number of the user.
     * @param {string} data.password - The password for the user.
     * @param {string} data.passwordConfirmation - The password confirmation.
     * @returns {Promise<Object>} User object or error response.
     */
    register: async function (data) {
      try {
        await $axios.get('/sanctum/csrf-cookie')
        const { status, data: { user } } = await $axios.post('/register', data)

        if (status === 200 && user && user.id) {
          // The backend's SpaRegisteredUserController already calls
          // Auth::login($user) on success, so the web session the PKCE
          // navigation rides on is established here too — no separate
          // /login round-trip needed. beginOAuthLogin navigates away; the
          // success toast/redirect happens in finishOAuthLogin on return.
          await this.beginOAuthLogin('register')
          return user
        }
      } catch (err) {
        return this.handleRequestError(err)
      }
    },

    /**
     * Log in a user
     *
     * @param {Object} data
     * @param {string} data.email - The email address of the user.
     * @param {string} data.password - The password for the user.
     * @returns {Promise<string>} The response from the server
     */
    login: async function (options) {
      try {
        await $axios.get('/sanctum/csrf-cookie')
        const { status, data } = await $axios.post('/login', options)

        if (status === 200 && data.mfaRequired) {
          // No client-side "pending MFA" state to track — the Laravel
          // session cookie already carries it server-side, and
          // POST /mfa/challenge rides on that same cookie. mfaMethods is
          // carried via the route query (not store state) so it survives a
          // hard reload of the challenge page, same as mfaRequired='1' on
          // the OAuth callback route.
          this.router.push({ name: 'MfaChallenge', query: { methods: (data.mfaMethods ?? []).join(',') } })
          return
        }

        if (status === 200 && data.user && data.user.id) {
          // beginOAuthLogin navigates away; the success toast/redirect
          // happens in finishOAuthLogin on return.
          await this.beginOAuthLogin('login')
          return data.user
        }
      } catch (err) {
        return this.handleRequestError(err)
      }
    },

    /**
     * Complete a pending MFA challenge (a code for the given method, or an
     * unused recovery code — the backend accepts either at this same
     * endpoint, under any confirmed method's `type`). Behaves like a normal
     * login from here: establishes the real OAuth2 tokens via the existing
     * PKCE flow.
     */
    // No try/catch here, unlike login/register — the backend's error shape
    // for a wrong code (a plain ValidationException message, not an i18n
    // key) doesn't fit handleRequestError's assumptions, so the caller
    // handles 422s itself (see PageMainMfaChallenge.vue), same division of
    // responsibility as UserStore's confirmMfaTotp/MfaEnrollmentDialog.
    submitMfaChallenge: async function ({ type, code }) {
      await $axios.get('/sanctum/csrf-cookie')
      const { data: { user } } = await $axios.post('/mfa/challenge', { type, code })
      await this.beginOAuthLogin('login')
      return user
    },

    /**
     * Request a code be emailed for the email MFA method (no send step
     * exists for totp — the authenticator app already has its code). No
     * try/catch here either: a 429 (backend throttles at 5/min) needs
     * different handling than a generic failure, which only the caller
     * (PageMainMfaChallenge.vue) has the UI context to decide.
     */
    sendMfaChallengeCode: async function ({ type }) {
      await $axios.get('/sanctum/csrf-cookie')
      const { data } = await $axios.post('/mfa/challenge/send', { type })
      return data
    },

    /**
     * Refresh token. Concurrent callers (e.g. several requests 401-ing at
     * once because the access token expired) all get the same in-flight
     * promise and its eventual result, rather than only the first caller
     * actually refreshing while the rest bail out early.
     */
    refresh () {
      if (!this.refreshToken) return Promise.resolve(false)
      if (refreshPromise) return refreshPromise

      refreshPromise = (async () => {
        try {
          const { status, data: tokens } = await $axios.post('/oauth/token', {
            grant_type: 'refresh_token',
            refresh_token: this.refreshToken,
            client_id: import.meta.env.OAUTH_CLIENT_ID
          })

          if (status === 200) {
            await this.storeTokens(tokens)
            $axios.defaults.headers.Authorization = `Bearer ${this.accessToken}`
            this.scheduleRefresh()
            return true
          }
          return false
        } catch (err) {
          if (!err.response) {
            // network or server unreachable
            console.warn('Refresh skipped: server unreachable')
            this.scheduleRetryRefresh(60 * 1000) // retry in 1 minute
            return false
          }

          // Maintenance mode: server is up but blocking all token requests.
          // The session is still valid — retry once maintenance ends.
          if (err.response.status === 503) {
            console.warn('Refresh skipped: server in maintenance mode')
            this.scheduleRetryRefresh(60 * 1000)
            return false
          }

          console.error('Token refresh failed:', err)
          // No live session to invalidate server-side (the refresh token
          // itself was rejected), so skip the network call — it would 419
          // on the CSRF-protected /logout route since there's no valid
          // session cookie to go with it.
          this.logout({ silent: true })
          return false
        } finally {
          refreshPromise = null
        }
      })()

      return refreshPromise
    },

    /**
     * Logout the user
     */
    logout: async function ({ silent = false } = {}) {
      if (!silent) {
        try {
          const { status, data: { fullName, message } } = await $axios.post('/logout')

          if (status === 200 && fullName && message) {
            this.$toast({
              type: 'info',
              title: t(`${message}.title`, { fullName }),
              caption: t(`${message}.caption`)
            })
          }
        } catch (err) {
          // Session may already be dead server-side (e.g. logging out after
          // an expired session) — that's fine, still clear local state below
          // rather than stranding the user on the current page.
          this.handleRequestError(err)
        }
      }

      // this.clearCookies()
      this.clearTokens()
      delete $axios.defaults.headers.Authorization

      // Flip isAuthenticated synchronously so the route guard's
      // isAuthPage redirect-to-Dashboard check doesn't see stale state
      // and bounce this navigation straight back.
      isAuthenticated.value = false

      // Clear the rest of the state after the route transition settles,
      // so resetting GlobalStore.instantSearchProps doesn't unmount
      // InstantSearch while a navigation is still mid-patch (see SANITETA-APP-3/4)
      await this.router.push({ name: 'Login' })
      await nextTick()
      this.$resetAll()
    },

    /**
     * Reset Password Request
     */
    forgotPassword: async function (data) {
      try {
        // await $axios.get('/sanctum/csrf-cookie')
        const { status, data: { message } } = await $axios.post('/forgot-password', data) // eslint-disable-line
        if (status === 200 && message) {
          this.router.push({ name: 'Login' })
          this.$toast({
            type: 'info',
            title: t(`${message}.title`),
            caption: t(`${message}.caption`)
          })
        }
      } catch (err) {
        return this.handleRequestError(err)
      }
    },

    /**
     * Reset Password
     */
    resetPassword: async function (data) {
      try {
        // await $axios.get('/sanctum/csrf-cookie')
        const { status, data: { message } } = await $axios.post('/reset-password', data) // eslint-disable-line
        if (status === 200 && message) {
          this.$toast({
            type: 'success',
            title: t(`${message}.title`),
            caption: t(`${message}.caption`)
          })
          this.router.push({ name: 'Login' })
        }
      } catch (err) {
        // An invalid/expired token is the one reset-password failure the
        // user can act on directly — offer to request a new link instead of
        // just reporting the dead end.
        const tokenErrorKey = err.response?.status === 422 ? err.response.data?.errors?.email?.[0] : null
        if (tokenErrorKey === 'Response.resetPassword.invalidOrExpiredToken') {
          this.$toast({
            type: 'error',
            title: t(`${tokenErrorKey}.title`),
            caption: t(`${tokenErrorKey}.caption`),
            autoClose: false,
            actions: [{
              label: t(`${tokenErrorKey}.action.label`),
              onClick: () => this.forgotPassword({ email: data.email })
            }]
          })
          return
        }
        return this.handleRequestError(err)
      }
    },

    resendVerificationEmail: async function (data) {
      try {
        // await $axios.get('/sanctum/csrf-cookie')
        const { status, data: { message } } = await $axios.post('/email/verification-notification', data) // eslint-disable-line
        if (status === 200 && message) {
          this.$toast({
            type: 'success',
            title: t(`${message}.title`),
            caption: t(`${message}.caption`)
          })
        }
      } catch (err) {
        return this.handleRequestError(err)
      }
    },

    restoreSession: async function () {
      $axios.defaults.headers.Authorization = `Bearer ${this.accessToken}`
      if (this.accessTokenExpiresAt && Date.now() < this.accessTokenExpiresAt) {
        if (!currentUserId.value) {
          await fetchUser()
        }
        this.scheduleRefresh()
      } else if (this.refreshToken) {
        this.refresh()
      }
    },

    scheduleRetryRefresh: function (delay) {
      this.clearRefreshTimer()
      this.refreshTimer = setTimeout(() => this.refresh(), delay)
    },

    scheduleRefresh: function () {
      this.clearRefreshTimer()
      if (!this.accessTokenExpiresAt) return

      const now = Date.now()
      const ttl = this.accessTokenExpiresAt - now
      // Refresh 2 min early, but never more than 20% of the token's own TTL —
      // a fixed 2 min buffer is longer than the ~1 min access token TTL used
      // locally, which made this delay permanently negative (refresh never fired).
      const buffer = Math.min(2 * 60 * 1000, ttl * 0.2)
      const refreshDelay = Math.max(ttl - buffer, 1000)

      if (ttl > 0) {
        this.refreshTimer = setTimeout(() => this.refresh(), refreshDelay)
      }
    },

    clearRefreshTimer: function () {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    }

  }
}
